import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useMe } from '@/api/domains/merchant-settings';
import { useDownloadLabels } from '@/api/domains/shipments';
import type { LabelFormat } from '@/api/types/common';
import type { Shipment } from '@/api/types/shipments';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toApiProblem } from '@/lib/errors';
import { LABEL_FORMAT_OPTIONS } from '@/lib/label-formats';

const FALLBACK_FORMAT: LabelFormat = 'application/pdf;format=A6';

function isPrintable(format: string): format is LabelFormat {
  return LABEL_FORMAT_OPTIONS.some((option) => option.value === format);
}

type Props = {
  /** Shipments to download; the dialog is open while this is non-null. */
  shipments: Shipment[] | null;
  onClose: () => void;
  onDownloaded?: () => void;
};

/**
 * Prompts for the label format (pre-selected to the account's configured default)
 * before downloading. While the carrier fetch + bundle runs it switches to a
 * non-dismissible spinner - bulk downloads of many orders can take a moment.
 */
export function LabelFormatDialog({ shipments, onClose, onDownloaded }: Props) {
  const { t } = useTranslation('shipments');
  const tCommon = useTranslation('common').t;
  const me = useMe();
  const download = useDownloadLabels();

  const settingDefault = me.data?.settings.default_label_format;
  const defaultFormat =
    settingDefault && isPrintable(settingDefault) ? settingDefault : FALLBACK_FORMAT;

  const [format, setFormat] = useState<LabelFormat>(defaultFormat);

  const open = shipments !== null;
  const count = shipments?.length ?? 0;

  // Re-seed with the account default each time the dialog opens.
  useEffect(() => {
    if (open) setFormat(defaultFormat);
  }, [open, defaultFormat]);

  const handleDownload = () => {
    if (!shipments) return;
    download.mutate(
      { shipments, format },
      {
        onSuccess: () => {
          toast.success(t('labelDownloaded'));
          onDownloaded?.();
          onClose();
        },
        onError: (error) => {
          const problem = toApiProblem(error);
          toast.error(t('labelFailed'), { description: problem.detail ?? problem.title });
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !download.isPending) onClose();
      }}
    >
      <DialogContent
        className="max-w-sm"
        onEscapeKeyDown={(event) => {
          if (download.isPending) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (download.isPending) event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>{t('labelFormatDialog.title')}</DialogTitle>
          <DialogDescription>
            {download.isPending
              ? t('labelDownload.description', { count })
              : t('labelFormatDialog.description', { count })}
          </DialogDescription>
        </DialogHeader>

        {download.isPending ? (
          <div className="flex justify-center py-6" role="status" aria-live="polite">
            <Loader2 className="size-8 animate-spin text-primary" aria-hidden />
          </div>
        ) : (
          <div className="space-y-2">
            <Label>{t('batch.labelFormat')}</Label>
            <Select value={format} onValueChange={(value) => setFormat(value as LabelFormat)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LABEL_FORMAT_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={download.isPending}>
            {tCommon('actions.cancel')}
          </Button>
          <Button onClick={handleDownload} disabled={download.isPending || count === 0}>
            {download.isPending ? '…' : t('detail.downloadLabel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
